'use client';

import { useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { Select, Option } from 'src/components/PulseUI';
import { Typography } from 'src/components';
import DateRangeLabel from './DateRangeLabel';
import { VOLUME_SELECT_OPTIONS, DEFAULT_DATE_RANGE } from 'src/components/constants';

interface DateRangeValue {
  gte: string;
  lte: string;
}

interface DateRangeFormProps {
  name: string;
  value?: DateRangeValue;
  tooltipText?: string;
}

const DateRangeForm = ({ name, value, tooltipText }: DateRangeFormProps) => {
  const { control } = useFormContext();

  const [error, setError] = useState<string | null>(null);

  const handleValidate = (values: DateRangeValue) => {
    const isValidLTE = values?.lte && values?.lte >= values?.gte;
    const isValidGTE = values?.gte && values?.gte <= values?.lte;

    if (!isValidGTE || !isValidLTE) {
      setError('Invalid values');
      return false;
    }
    setError(null);
    return true;
  };

  return (
    <div className="w-full" data-testid="date-range-form">
      <Controller
        name={name}
        control={control}
        defaultValue={value || DEFAULT_DATE_RANGE}
        rules={{
          validate: (values: DateRangeValue) => handleValidate(values),
        }}
        render={({ field }) => (
          <fieldset className="grid grid-cols-12 gap-2">
            <div className="col-span-6">
              <DateRangeLabel label="From" tooltipText={tooltipText} />
              <Select
                id={`${name}-from`}
                aria-label="From date"
                size="sm"
                value={field?.value?.gte}
                onChange={(_, newValue) => {
                  field.onChange({ ...field.value, gte: newValue });
                }}
              >
                {VOLUME_SELECT_OPTIONS?.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </div>
            <div className="col-span-6">
              <DateRangeLabel label="Through" tooltipText={tooltipText} />
              <Select
                id={`${name}-through`}
                aria-label="Through date"
                size="sm"
                value={field?.value?.lte}
                onChange={(_, newValue) =>
                  field.onChange({ ...field.value, lte: newValue })
                }
              >
                {VOLUME_SELECT_OPTIONS?.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </div>
          </fieldset>
        )}
      />
      {error && (
        <div role="alert" data-testid="date-range-error">
          <Typography variant="caption" color="danger">
            {error}
          </Typography>
        </div>
      )}
    </div>
  );
};

export default DateRangeForm;
